/**
 * Amazon Connect Chat integration
 * Dynamically loads and initializes the Amazon Connect chat widget
 */

interface AmazonConnectConfig {
  baseHostname: string;
  widgetId: string;
  snippetId: string;
}

/**
 * Get environment-specific Amazon Connect configuration
 */
const getAmazonConnectConfig = (): AmazonConnectConfig => {
  const origin = window.location.origin;

  switch (origin) {
    case 'https://portal.unum.com':
      return {
        baseHostname: 'unmuusccprod.my.connect.aws',
        widgetId: '2dce729c-d05d-42ed-a1a5-50ebe81819d9',
        snippetId:
          'QVFJREFIaXdlaTllQXR1SnQyK1JZc1Z3dWE3clBZQjQvWm15emlVb25scEltc3BJNmdHZy9sbXFOM3d4STlkK3BkSFUxNGVGQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNa2hxSWppciszSjdmdk1ZQUFnRVFnQ3ZNR0s0QUZJTFpCZHkva214YzJ2U0doREZZQWpnUjAxY1E5TmZQcWNxREdPMHpuUkpaUHRxbW1SMnI6OlVUMGYyWDZpR0JKb2gyZmtONU5GeTBrVWdvdk1MV25rK2Z6SjEyaWY1U3YyS291ZlN6dTBWTFVNaXQ3UW90c1c0SmNpZjhocWtxendyQnI5Unl2eFVIOTI3WFR6Z1lYUmhIOGpEWnAxb2NSWDRLbzZrN2J5bDA3WFNTVGZNUlNsNmttelNkVXBQQmlMMkVFTzRlSUtscWYycHQzamExND0=',
      };
    case 'https://portal-ua.unum.com':
      return {
        baseHostname: 'unmuusccacpt.my.connect.aws',
        widgetId: '56f48523-70e4-47c0-bc68-7ca25a7d4d0f',
        snippetId:
          'QVFJREFIaFg3VVJ2dFRXb2IrUUdndUkvUzRJMnRnQ1NJL3Erc1JKT0lFQ3lCQlNlUEFGakFnRkJLSVVBTGYvNFpSOXlMbk82QUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNZE5ZeWF3QXlpQW9OdWdXbEFnRVFnQ3V5a0pRSTgvV2Jjczk0cGRmUU01UjlJZDlCS2IzRkxvWUo0enMvL2I1TlFwc2EyWndCaUE4NEQ5c0o6Ojg1ZkhDN1ZSL0Uway9rVTV1WnNoR0lFTG1yZWFGRzJuaTZSVVpVcjUyaDlDaXBCdkxHSG54Sjd3YTUxWEJiMVJyZjNxMThMNENuNjBHTTBnU0VDQzhWa0I2ZlN4UHJEYkRWTXc3ZEEwcW16OGxST0lwVWNsZENHaGhJRGlpdDNsOFlDM2NxN3ZjYi9VekdZOWRWUkpMa0lhRjROcnpSQT0=',
      };
    default:
      return {
        baseHostname: 'unmuusccdev.my.connect.aws',
        widgetId: 'c0dc9436-95cc-4456-a081-368a388a0a88',
        snippetId:
          'QVFJREFIaXdlaTllQXR1SnQyK1JZc1Z3dWE3clBZQjQvWm15emlVb25scEltc3BJNmdFSjlQOEV2bGVQM1BmSWdiS3Z3R2d0QUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNT01WTUF0ZzBJVjB1U1U2ZUFnRVFnQ3RRb2IvNHBpZE5RekVJVldyTjFRdFU5dG4rL3ZvTmRQSlo3bVBrc2U2RW15MDJ3Z0oxMTdJRjAyay86OkJnSTk1SVBVMjNCRGYxQnZjSmdOenQ2YmxjWmRDbkZEdC9vLzJnb3JCTzdwdTJMdEV3NWVIQVoyczJ1U1ZiNnM2SHVaUlZjS2VMRmM3L3UyeVZyWnU1MlBlZS9mS3VtQkdiSkxUbFhvaFN6ejFtZWVKb00yeUFFMDZRMWdpOEFDNk55dDFxS1FKdDd5UlZiY2ZxMklUSFZhdy9RMk9vYz0=',
      };
  }
};

/**
 * Inject styles to hide the chat widget initially
 */
const injectChatWidgetStyles = () => {
  // Check if style already exists
  if (document.getElementById('amazon-connect-chat-widget-styles')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'amazon-connect-chat-widget-styles';
  style.textContent = `
    #amazon-connect-chat-widget {
      display: none;
    }
  `;
  document.head.appendChild(style);
};

/**
 * Load the Amazon Connect chat interface script
 */
const loadAmazonConnectScript = (
  config: AmazonConnectConfig,
  firstName: string,
  lastName: string,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if ((window as any).amazon_connect) {
      resolve();
      return;
    }

    // Inject styles to hide the widget initially
    injectChatWidgetStyles();

    // Initialize the amazon_connect function queue BEFORE loading the script
    // The widget checks for window.amazon_connect.ac array on load
    const amazonConnect: any = function (...args: any[]) {
      amazonConnect.ac.push(args);
    };
    amazonConnect.ac = [];
    (window as any).amazon_connect = amazonConnect;

    // Configure the widget BEFORE the script loads and initializes
    configureAmazonConnect(config, firstName, lastName);

    const script = document.createElement('script');
    script.src = `https://${config.baseHostname}/connectwidget/static/amazon-connect-chat-interface-client.js`;
    script.async = true;
    script.id = config.widgetId;

    script.onload = () => {
      resolve();
    };

    script.onerror = () => {
      reject(new Error('Failed to load Amazon Connect script'));
    };

    document.head.appendChild(script);
  });
};

/**
 * Configure the Amazon Connect chat widget
 */
const configureAmazonConnect = (
  config: AmazonConnectConfig,
  firstName: string,
  lastName: string,
) => {
  const amazonConnect = (window as any).amazon_connect;

  if (!amazonConnect) {
    console.error('Amazon Connect not available');
    return;
  }

  // Configure styles
  amazonConnect('styles', {
    iconType: 'CHAT',
    openChat: { color: '#ffffff', backgroundColor: '#015294' },
    closeChat: { color: '#ffffff', backgroundColor: '#015294' },
  });

  amazonConnect('customStyles', {
    global: {
      frameWidth: '400px',
      frameHeight: '700px',
      textColor: '#525252',
      fontSize: '16px',
      footerHeight: '120px',
    },
    header: {
      headerTextColor: '#ffffff',
    },
    footer: {
      buttonBackgroundColor: '#015294',
      buttonTextColor: '#ffffff',
    },
  });

  amazonConnect('customizationObject', {
    transcript: {
      hideDisplayNames: false,
      eventNames: {
        customer: 'You',
        agent: 'Unum',
      },
      eventMessages: {
        participantJoined: '{name} joined the chat',
        participantDisconnect: '',
        participantLeft: '{name} dropped from the chat',
        participantIdle: 'Are you still there?',
        participantReturned: '',
        chatEnded: 'Chat ended',
      },
    },
    composer: {
      disableEmojiPicker: true,
      disableCustomerAttachments: true,
      alwaysHideToolbar: true,
    },
    endChat: {
      enableConfirmationDialog: true,
      confirmationDialogText: {
        title: 'End Chat',
        message: 'Are you sure you want to end this chat session?',
        confirmButtonText: 'End Chat',
        cancelButtonText: 'Cancel',
      },
    },
  });

  amazonConnect('snippetId', config.snippetId);

  amazonConnect('supportedMessagingContentTypes', [
    'text/plain',
    'text/markdown',
    'application/vnd.amazonaws.connect.message.interactive',
    'application/vnd.amazonaws.connect.message.interactive.response',
  ]);

  // Set up authentication
  amazonConnect('authenticate', function (callback: any) {
    window.fetch('/api/chats', { method: 'POST' }).then((res) => {
      res.json().then((data) => {
        callback(data.jwt);
      });
    });
  });

  // Set customer display name
  amazonConnect('customerDisplayName', (callback: any) => {
    const name = `${firstName} ${lastName}`;
    console.log('Amazon Connect: Setting customer display name:', name);
    callback(name);
  });
};

/**
 * Initialize Amazon Connect chat with user information
 * @param firstName - User's first name
 * @param lastName - User's last name
 */
export const initializeCommonChat = async (
  firstName: string,
  lastName: string,
): Promise<void> => {
  try {
    const config = getAmazonConnectConfig();
    await loadAmazonConnectScript(config, firstName, lastName);
  } catch (error) {
    console.error('Failed to initialize Amazon Connect chat:', error);
  }
};

const CHAT_LAUNCHER_HIDDEN_STYLE_ID = 'amazon-connect-launcher-hidden-styles';

/**
 * Show or hide the Amazon Connect launcher (open widget) button.
 * Uses a CSS rule rather than toggling the element directly, since the button
 * is injected asynchronously by the Amazon Connect script and may not exist yet.
 * @param visible - Whether the launcher button should be visible
 */
export const setChatLauncherVisible = (visible: boolean): void => {
  const existing = document.getElementById(CHAT_LAUNCHER_HIDDEN_STYLE_ID);

  if (visible) {
    existing?.remove();
    return;
  }

  if (existing) {
    return;
  }

  const style = document.createElement('style');
  style.id = CHAT_LAUNCHER_HIDDEN_STYLE_ID;
  style.textContent = `
    #amazon-connect-open-widget-button {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
};
